import type { AlertRule, AlertSchedule, AlertSource } from '../types/gotypes'
import type { AlertTableInput, CreateAlertBody, UpdateAlertBody } from '../hooks/useAlerts'
import { alertSourceOf } from './alertSources'
import { isValidAt, sameSchedule, scheduleBody } from './alertSchedule'

/**
 * The alert card on the agent start screen (`/agent/:customer/new?alert=…`):
 * what a table or question alert watches, when, and who else hears. Nothing
 * is saved until Send; then the card's contents become the rule (POST, or a
 * PUT of what changed for `?alert=<id>`) and the agent writes its check.
 * Event alerts are made in the Alerts page dialog: they need no agent.
 */
export type AlertSetupKind = 'table' | 'query'

export interface AlertSetupDraft {
  name: string
  kind: AlertSetupKind
  /** The job every table (or the question) is on, and the chat's one job. */
  job: string
  tables: AlertTableInput[]
  question: string
  condition: string
  context: string
  schedule: AlertSchedule
  recipients: string[]
}

export type AlertDraftField = 'name' | 'job' | 'tables' | 'question' | 'condition' | 'schedule'
export type AlertDraftProblems = Partial<Record<AlertDraftField, string>>

export function emptyAlertDraft(job: string, schedule: AlertSchedule): AlertSetupDraft {
  return { name: '', kind: 'table', job, tables: [], question: '', condition: '', context: '', schedule, recipients: [] }
}

const tableInput = (t: AlertTableInput): AlertTableInput => ({
  job: t.job,
  folder_path: t.folder_path || '',
  filename: t.filename,
  display_name: t.display_name,
})

const tableKey = (t: AlertTableInput) => `${t.job}\u0000${t.folder_path || ''}\u0000${t.filename}`

export const sameDraftTable = (a: AlertTableInput, b: AlertTableInput) => tableKey(a) === tableKey(b)

/** The tables with `t` appended, unless it is already there. */
export function addDraftTable(tables: AlertTableInput[], t: AlertTableInput): AlertTableInput[] {
  return tables.some((x) => sameDraftTable(x, t)) ? tables : [...tables, tableInput(t)]
}

const sameTableSet = (a: AlertTableInput[], b: AlertTableInput[]) =>
  a.length === b.length && a.map(tableKey).sort().join('\n') === b.map(tableKey).sort().join('\n')

const sameEmails = (a: string[], b: string[]) => [...a].sort().join('\n') === [...b].sort().join('\n')

/** A saved table or question alert, as the card shows it. `fallback` stands in for a rule with no schedule. */
export function draftFromRule(rule: AlertRule, fallback: AlertSchedule): AlertSetupDraft {
  const source = alertSourceOf(rule)
  return {
    name: rule.name ?? '',
    kind: source.kind === 'query' ? 'query' : 'table',
    job: source.job,
    tables: (rule.tables ?? []).map(tableInput),
    question: source.query?.question ?? '',
    condition: rule.config?.natural_language?.rule_text ?? '',
    context: rule.config?.natural_language?.context ?? '',
    schedule: rule.config?.schedule ? scheduleBody(rule.config.schedule) : fallback,
    recipients: rule.recipients ?? [],
  }
}

/** Why Send cannot save the card yet, per field; empty when it can. */
export function draftProblems(d: AlertSetupDraft): AlertDraftProblems {
  const problems: AlertDraftProblems = {}
  if (!d.name.trim()) problems.name = 'Give the alert a name.'
  if (!d.job) problems.job = 'Pick a job.'
  if (d.kind === 'table' && d.tables.length === 0) problems.tables = 'Pick at least one saved table.'
  if (d.kind === 'query' && !d.question.trim()) problems.question = "Ask a question about the job's data."
  if (!d.condition.trim()) problems.condition = 'Say when you want to be told.'
  if (!isValidAt(d.schedule.at)) problems.schedule = 'Pick a time, such as 09:00.'
  return problems
}

const sourceOf = (d: AlertSetupDraft): AlertSource =>
  d.kind === 'query' ? { kind: 'query', job: d.job, query: { question: d.question.trim() } } : { kind: 'table', job: d.job }

/** POST /alerts/:customer for the card. */
export function createAlertBody(d: AlertSetupDraft): CreateAlertBody {
  return {
    name: d.name.trim(),
    source: sourceOf(d),
    ...(d.kind === 'table' ? { tables: d.tables.map(tableInput) } : {}),
    rule_text: d.condition.trim(),
    ...(d.context.trim() ? { context: d.context.trim() } : {}),
    schedule: scheduleBody(d.schedule),
    recipients: d.recipients,
  }
}

/**
 * PUT /alerts/:customer/:id for `?alert=<id>`: only what differs from the
 * saved rule, so the server clears the check only when the tables, question
 * or condition really changed (alerts.UpdateClearsCheck).
 */
export function updateAlertBody(d: AlertSetupDraft, saved: AlertSetupDraft): UpdateAlertBody {
  const body: UpdateAlertBody = {}
  if (d.name.trim() !== saved.name.trim()) body.name = d.name.trim()
  if (d.job !== saved.job || (d.kind === 'query' && d.question.trim() !== saved.question.trim())) body.source = sourceOf(d)
  if (d.kind === 'table' && !sameTableSet(d.tables, saved.tables)) body.tables = d.tables.map(tableInput)
  if (d.condition.trim() !== saved.condition.trim()) body.rule_text = d.condition.trim()
  if (d.context.trim() !== saved.context.trim()) body.context = d.context.trim()
  if (!sameSchedule(d.schedule, saved.schedule)) body.schedule = scheduleBody(d.schedule)
  if (!sameEmails(d.recipients, saved.recipients)) body.recipients = d.recipients
  return body
}
